/**
 * KeysTab — PK / FK candidates for the currently selected table.
 * Data flows in from DataProfilerPage via the session's relationships
 * payload (pk_candidates / fk_candidates keyed by table name).
 *
 * Each candidate carries a 0–1 confidence score from the detector:
 *   • declared PK (DB constraint) always scores 1.0
 *   • inferred PK scores from uniqueness + non-null rate + name heuristic
 *   • inferred FK scores from name + type + value-overlap match against
 *     a PK candidate in another table
 */

import { HelpCircle } from "lucide-react";
import type { FKCandidate, KeyCandidate } from "./DetailPanel";

const PK_HELP =
  "Confidence = how certain the detector is that this column (or set of columns) uniquely identifies every row. Declared database PKs always score 100%. Inferred candidates blend uniqueness ratio (distinct / non-null), non-null rate, and name heuristics (e.g. columns ending in '_id').";
const FK_HELP =
  "Confidence = strength of the FK inference: name match (e.g. customer_id → customers.id), type compatibility, and observed value overlap (Jaccard) between source and target columns. Declared database FKs always score 100%.";

function ConfidenceBar({ value, tip }: { value: number; tip?: string }) {
  const pct = Math.round(value * 100);
  const tone =
    pct >= 80 ? "bg-emerald-400" : pct >= 60 ? "bg-yellow-400" : "bg-white/40";
  return (
    <div
      className="flex items-center gap-2"
      title={
        tip ||
        (pct >= 80
          ? `Confidence ${pct}% — high, acceptable candidate.`
          : pct >= 60
            ? `Confidence ${pct}% — medium, worth reviewing.`
            : `Confidence ${pct}% — low, likely not a real key.`)
      }
    >
      <div className="h-1.5 w-20 rounded-full bg-white/10">
        <div
          className={`h-1.5 rounded-full ${tone}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-white/60">{pct}%</span>
    </div>
  );
}

export interface KeysTabProps {
  pkCandidates?: KeyCandidate[];
  fkCandidates?: FKCandidate[];
}

export function KeysTab({
  pkCandidates = [],
  fkCandidates = [],
}: KeysTabProps) {
  return (
    <div className="space-y-5">
      <section>
        <h3 className="text-sm font-semibold text-white/90 mb-1 flex items-center gap-1.5">
          Primary Key Candidates{" "}
          <span className="text-xs text-white/40">({pkCandidates.length})</span>
          <HelpCircle
            className="w-3 h-3 text-white/40 cursor-help"
            aria-label="Primary Key confidence explained"
            // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
            title={PK_HELP}
          />
        </h3>
        <p
          className="text-[11px] text-white/50 mb-2 leading-snug"
          title={PK_HELP}
        >
          Columns the profiler believes uniquely identify a row. The bar shows
          confidence — 100% means a declared database constraint.
        </p>
        {pkCandidates.length === 0 ? (
          <p className="text-xs text-white/50">
            No PK candidate detected for this table.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {pkCandidates.map((pk, i) => (
              <li
                key={i}
                className="py-1.5 border-b border-white/5 last:border-0"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[13px] text-white/85 truncate">
                      {(pk.columns ?? []).join(", ") || "—"}
                    </span>
                    {pk.type === "declared" && (
                      <span
                        className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-400/15 text-emerald-300"
                        title="Declared in the database schema — authoritative"
                      >
                        declared
                      </span>
                    )}
                    {pk.type === "inferred" && (
                      <span
                        className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/55"
                        title="Inferred by the profiler from uniqueness + nullability"
                      >
                        inferred
                      </span>
                    )}
                  </div>
                  <ConfidenceBar value={pk.confidence ?? 0} />
                </div>
                {pk.evidence && pk.evidence.length > 0 && (
                  <ul className="mt-1 ml-1 text-[11px] text-white/45 list-disc list-inside space-y-0.5">
                    {pk.evidence.slice(0, 3).map((e, j) => (
                      <li key={j}>{e}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-white/90 mb-1 flex items-center gap-1.5">
          Foreign Key Candidates{" "}
          <span className="text-xs text-white/40">({fkCandidates.length})</span>
          <HelpCircle
            className="w-3 h-3 text-white/40 cursor-help"
            aria-label="Foreign Key confidence explained"
            // @ts-expect-error mypy cleanup — runtime contract is correct, type model needs follow-up
            title={FK_HELP}
          />
        </h3>
        <p
          className="text-[11px] text-white/50 mb-2 leading-snug"
          title={FK_HELP}
        >
          Columns that likely reference a PK in another table. Confidence
          combines name match, type compatibility, and value-overlap.
        </p>
        {fkCandidates.length === 0 ? (
          <p className="text-xs text-white/50">
            No FK candidates inferred for this table.
          </p>
        ) : (
          <ul className="space-y-2">
            {fkCandidates.map((fk, i) => (
              <li
                key={i}
                className="py-1.5 border-b border-white/5 last:border-0"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono text-[13px] text-white/85">
                    {(fk.columns ?? []).join(", ") || "—"}
                    <span className="text-white/40"> → </span>
                    <span className="text-[var(--primary)]">
                      {fk.target_table}
                      {fk.target_columns && fk.target_columns.length > 0 && (
                        <span className="text-white/40">
                          .{fk.target_columns.join(",")}
                        </span>
                      )}
                    </span>
                  </div>
                  <ConfidenceBar value={fk.confidence ?? 0} />
                </div>
                {fk.evidence && fk.evidence.length > 0 && (
                  <ul className="mt-1 ml-1 text-[11px] text-white/45 list-disc list-inside space-y-0.5">
                    {fk.evidence.slice(0, 3).map((e, j) => (
                      <li key={j}>{e}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
